/**
 * Deterministik rastgelelik.
 * Aynı seed her zaman aynı dünyayı üretir; kaydedilen dünya tekrar açıldığında
 * arazi/kaynak yerleşimi birebir aynı gelir. Math.random() dünya üretiminde
 * ASLA kullanılmaz (yalnızca görsel/tek seferlik süslemelerde kullanılabilir).
 */

/** Hızlı, iyi dağılımlı 32-bit PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Metinden 32-bit seed (kullanıcının yazdığı dünya seed'i için). */
export function hashString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Konum + seed'den türetilmiş alt-seed (chunk başına bağımsız RNG için). */
export function seedAt(seed: number, x: number, y: number, salt = 0): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = (Math.imul(h ^ (x | 0), 0x85ebca6b) ^ (x >> 16)) >>> 0;
  h = (Math.imul(h ^ (y | 0), 0xc2b2ae35) ^ (y >> 16)) >>> 0;
  h = (Math.imul(h ^ (salt | 0), 0x27d4eb2f) ^ (salt >> 13)) >>> 0;
  return h >>> 0;
}

/** 0..1 arası deterministik değer (grid noktası için). */
function hash2(seed: number, x: number, y: number): number {
  return seedAt(seed, x, y) / 4294967296;
}

/** Gradyan vektörü (Perlin benzeri). */
function grad2(seed: number, x: number, y: number, dx: number, dy: number): number {
  const a = hash2(seed, x, y) * Math.PI * 2;
  return Math.cos(a) * dx + Math.sin(a) * dy;
}

const fade = (t: number) => t * t * t * (t * (t * 6 - 15) + 10);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** 2B gradyan gürültüsü, -1..1 aralığında. */
export function noise2(seed: number, x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = fade(xf);
  const v = fade(yf);
  const n00 = grad2(seed, xi, yi, xf, yf);
  const n10 = grad2(seed, xi + 1, yi, xf - 1, yf);
  const n01 = grad2(seed, xi, yi + 1, xf, yf - 1);
  const n11 = grad2(seed, xi + 1, yi + 1, xf - 1, yf - 1);
  return lerp(lerp(n00, n10, u), lerp(n01, n11, u), v);
}

export interface FbmOptions {
  octaves?: number;
  frequency?: number;
  amplitude?: number;
  lacunarity?: number;
  gain?: number;
}

/** Katmanlı gürültü (arazi yüksekliği, nem, sıcaklık haritaları için). */
export function fbm(seed: number, x: number, y: number, opts: FbmOptions = {}): number {
  const { octaves = 4, frequency = 1, amplitude = 1, lacunarity = 2, gain = 0.5 } = opts;
  let f = frequency;
  let a = amplitude;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += noise2(seed + i * 7919, x * f, y * f) * a;
    norm += a;
    f *= lacunarity;
    a *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

/** Sırt gürültüsü: nehir yatakları ve dağ sırtları için. */
export function ridged(seed: number, x: number, y: number, opts: FbmOptions = {}): number {
  const { octaves = 3, frequency = 1, lacunarity = 2, gain = 0.5 } = opts;
  let f = frequency;
  let a = 1;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    const n = 1 - Math.abs(noise2(seed + i * 6151, x * f, y * f));
    sum += n * n * a;
    norm += a;
    f *= lacunarity;
    a *= gain;
  }
  return norm > 0 ? sum / norm : 0;
}

/** Ağırlıklı seçim (loot tabloları, balık nadirlikleri). */
export function weightedPick<T>(rand: () => number, entries: ReadonlyArray<{ weight: number; value: T }>): T | null {
  let total = 0;
  for (const e of entries) total += Math.max(0, e.weight);
  if (total <= 0) return null;
  let r = rand() * total;
  for (const e of entries) {
    r -= Math.max(0, e.weight);
    if (r <= 0) return e.value;
  }
  return entries[entries.length - 1]!.value;
}

export const randRange = (rand: () => number, min: number, max: number) => min + rand() * (max - min);
export const randInt = (rand: () => number, min: number, max: number) => Math.floor(randRange(rand, min, max + 1));
